import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectProps } from "@custom-types/project-types";
import { Accordion, Alert } from "@flat-ui";
import { Skeleton } from "@mui/material";
import { ProjectMemberList } from "@pages/projects/project-member-list";
import {
  projectMembersSelector,
  selectedProjectIdSelector,
} from "@store/projects/projects-selector";
import { fetchProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useState } from "react";

type FetchStatus = "pending" | "succeeded" | "rejected";

interface Props extends BaseProjectProps {
  /** Location from which the accordion is opened, for tracking purposes. */
  location: "share-project-dialog";
}

/** Renders the members of a project in the share dialog */
export function ProjectMembersAccordion({
  project,
  location,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const projectMembers = useAppSelector(projectMembersSelector);
  const selectedProjectId = useAppSelector(selectedProjectIdSelector);

  // The project is already the selected one (e.g. on the project overview page),
  // so its members are loaded and there is no need to fetch them again.
  const areMembersAlreadyLoaded = selectedProjectId === project.id;

  const [fetchStatus, setFetchStatus] = useState<FetchStatus>(
    areMembersAlreadyLoaded ? "succeeded" : "pending"
  );

  const sortedMembers = sortMembersByNameOrEmail([...projectMembers]);

  // Fetch the project details to load its members and mark it as the selected
  // project so the invite dialog stays in sync. Skipped when the project is
  // already selected, since re-fetching would toggle the global project loading
  // flag and unmount the share dialog on the project overview page.
  useEffect(() => {
    if (areMembersAlreadyLoaded) {
      return;
    }

    async function fetchMembers(): Promise<void> {
      setFetchStatus("pending");

      try {
        await dispatch(
          fetchProjectDetails({ companyId, projectId: project.id })
        ).unwrap();

        setFetchStatus("succeeded");
      } catch (error) {
        setFetchStatus("rejected");

        handleErrorWithToast({
          id: `ProjectMembers-fetchMembers-${Date.now().toString()}`,
          title: "Could not fetch project members",
          error,
        });
      }
    }

    void fetchMembers();
  }, [
    areMembersAlreadyLoaded,
    dispatch,
    companyId,
    project.id,
    handleErrorWithToast,
  ]);

  if (fetchStatus === "pending") {
    return (
      <Skeleton
        variant="rectangular"
        height="48px"
        sx={{ marginTop: SPACE_ELEMENTS_OF_MODAL }}
      />
    );
  }

  if (fetchStatus === "rejected") {
    return (
      <Alert
        variant="warning"
        title="Project members could not be fetched"
        sx={{ marginTop: SPACE_ELEMENTS_OF_MODAL }}
      />
    );
  }

  return (
    <Accordion
      title={`${sortedMembers.length} people are invited`}
      onChange={(isExpanded) => {
        trackEvent({
          name: ProjectEvents.toggleProjectMembers,
          props: { isExpanded },
        });
      }}
      sx={{
        border: "none",
        "& .MuiButtonBase-root": {
          padding: "0px",
          flexDirection: "row-reverse",
          justifyContent: "flex-end",
          gap: 0.5,
        },
        "& .MuiAccordionSummary-expandIconWrapper svg": {
          fontSize: "18px",
        },
      }}
    >
      <ProjectMemberList
        project={project}
        members={sortedMembers}
        location={location}
      />
    </Accordion>
  );
}
